/**
 * F2.4b2c / F2.4b3 / F2.5a3 — Affichage fidélité ticket caisse (miroir backend).
 * Aucun rewardId / effectType / baseCalculee dans les libellés utilisateur.
 * % : lecture snapshot.pourcentage + réduction € snapshot — jamais de recalcul.
 */

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function formatPctLabel(pourcentage) {
  const p = Number(pourcentage);
  if (!Number.isFinite(p) || !(p > 0)) return null;
  return Number.isInteger(p) ? String(p) : String(p);
}

/**
 * Lignes affichage phase applied (snapshot).
 * EUR : format historique inchangé.
 * % : « Récompense fidélité — X % : −Y € » + points.
 */
function buildAppliedLines({ libelle, ptsOk, reductionEur, pourcentage, totalFinal, pointsRecredites, commandeAnnulee }) {
  const lines = [];
  const pctLabel = formatPctLabel(pourcentage);
  if (pctLabel && reductionEur > 0) {
    lines.push(`Récompense fidélité — ${pctLabel} % : −${reductionEur.toFixed(2)} €`);
    if (ptsOk != null) lines.push(`${ptsOk} points utilisés`);
  } else {
    lines.push(`Fidélité : ${libelle}`);
    if (ptsOk != null) lines.push(`Points utilisés : ${ptsOk}`);
    if (reductionEur > 0) lines.push(`Réduction : −${reductionEur.toFixed(2)} €`);
  }
  if (pointsRecredites != null && pointsRecredites > 0) {
    lines.push(`Points recrédités : ${pointsRecredites}`);
    if (commandeAnnulee) {
      lines.push('Récompense restaurée (annulation / remboursement)');
    }
  }
  if (totalFinal != null) lines.push(`Total : ${totalFinal.toFixed(2)} €`);
  return lines;
}

/**
 * @param {object} opts
 * @param {object|null} opts.commande
 * @param {string|null} opts.rewardIdIntent
 * @param {object|null} opts.consommation
 * @param {string|null} opts.intentLibelle
 */
export function buildCaisseFideliteDisplay({
  commande = null,
  rewardIdIntent = null,
  consommation = null,
  intentLibelle = null,
} = {}) {
  const totalFinal =
    commande?.total != null && Number.isFinite(Number(commande.total))
      ? roundMoney(commande.total)
      : null;

  const paid =
    commande?.statutPaiement === 'VALIDE' ||
    commande?.statut === 'ANNULEE' ||
    (consommation && (consommation.pointsDebites > 0 || consommation.reductionEur > 0));

  if (paid && consommation) {
    const snap =
      consommation.snapshot && typeof consommation.snapshot === 'object'
        ? consommation.snapshot
        : {};
    const libelle =
      (snap.libelle && String(snap.libelle).trim()) || 'Récompense fidélité';
    const points = Number(consommation.pointsDebites);
    const reductionEur = roundMoney(
      consommation.reductionEur != null
        ? consommation.reductionEur
        : snap.reductionEurAppliquee != null
          ? snap.reductionEurAppliquee
          : commande?.reductionAvantage
    );
    const ptsOk = Number.isFinite(points) && points > 0 ? Math.floor(points) : null;
    const restauration = consommation.restauration || null;
    const pointsRecredites =
      restauration && Number.isFinite(Number(restauration.pointsRecredites))
        ? Math.floor(Number(restauration.pointsRecredites))
        : null;
    const pourcentage = snap.pourcentage;
    const lines = buildAppliedLines({
      libelle,
      ptsOk,
      reductionEur,
      pourcentage,
      totalFinal,
      pointsRecredites,
      commandeAnnulee: commande?.statut === 'ANNULEE',
    });
    return {
      phase: 'applied',
      libelle,
      points: ptsOk,
      reductionEur:
        reductionEur > 0 ? reductionEur : roundMoney(commande?.reductionAvantage || 0),
      pourcentage: formatPctLabel(pourcentage) ? Number(pourcentage) : null,
      totalFinal,
      restored: Boolean(restauration),
      pointsRecredites,
      lines,
    };
  }

  if (paid && Number(commande?.reductionAvantage) > 0) {
    const reductionEur = roundMoney(commande.reductionAvantage);
    const points =
      commande.avantagePointsUtilises != null
        ? Math.floor(Number(commande.avantagePointsUtilises))
        : null;
    const libelle = 'Récompense fidélité';
    const lines = buildAppliedLines({
      libelle,
      ptsOk: points > 0 ? points : null,
      reductionEur,
      pourcentage: null,
      totalFinal,
      pointsRecredites: null,
      commandeAnnulee: false,
    });
    return {
      phase: 'applied',
      libelle,
      points: points > 0 ? points : null,
      reductionEur,
      pourcentage: null,
      totalFinal,
      restored: false,
      pointsRecredites: null,
      lines,
    };
  }

  if (rewardIdIntent && !paid) {
    const libelle =
      (intentLibelle && String(intentLibelle).trim()) || 'Récompense sélectionnée';
    return {
      phase: 'intent',
      libelle,
      points: null,
      reductionEur: null,
      pourcentage: null,
      totalFinal: null,
      restored: false,
      pointsRecredites: null,
      lines: [
        `Intention fidélité : ${libelle}`,
        'Aucun débit tant que le ticket n’est pas encaissé',
      ],
    };
  }

  return {
    phase: null,
    libelle: null,
    points: null,
    reductionEur: null,
    pourcentage: null,
    totalFinal,
    restored: false,
    pointsRecredites: null,
    lines: [],
  };
}

/** HTML duplicata — lignes utilisateur uniquement (pas de rewardId). */
export function formatFideliteDuplicataBlock(display) {
  if (!display?.phase || !Array.isArray(display.lines) || display.lines.length === 0) {
    return '';
  }
  const body = display.lines
    .filter((l) => !String(l).startsWith('Total :'))
    .map((l) => `<div>${escapeHtml(l)}</div>`)
    .join('');
  return `<div style="margin-top:10px;border-top:1px dashed #000;padding-top:6px">${body}</div>`;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export { roundMoney };
